// 사진 업로드 전 줄이기. 차량 사진과 휴양소 사진이 같이 쓴다.
// 휴대폰 원본(3~5MB)을 그대로 올리면 모바일망에서 실패가 잦고 보는 쪽도 느리다.
// 사용자가 화질을 고르지 않는다. 증빙 사진은 사람마다 품질이 달라지면 안 되므로 용도별로 정해 둔다.

export interface PhotoQuality {
  /** 긴 변 최대 픽셀. 더 작은 사진은 키우지 않는다 */
  maxEdge: number;
  /** JPEG 품질 (0~1) */
  quality: number;
}

/** 전체 모습 사진: 차량 앞/뒤/좌/우, 휴양소 입실·퇴실 사진. 한 장 약 0.6~1MB */
export const OVERVIEW_QUALITY: PhotoQuality = { maxEdge: 2048, quality: 0.85 };
/** 근접 사진: 차량 추가 사진(흠집). 세부가 중요하고 장수가 적어 더 높게 둔다 */
export const DETAIL_QUALITY: PhotoQuality = { maxEdge: 3000, quality: 0.85 };

function loadImage(objectUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("이미지를 읽지 못했습니다."));
    image.src = objectUrl;
  });
}

/**
 * 긴 변과 JPEG 품질을 정해진 값으로 줄인다. 브라우저가 디코딩하지 못하는 형식이면 원본을 그대로 돌려준다.
 * EXIF 회전은 최신 브라우저가 img 를 그릴 때 알아서 반영한다.
 * 다시 저장하면 사진 안의 촬영 정보가 지워지므로, 필요하면 줄이기 전에 readCaptureTime 으로 읽어 둔다.
 */
export async function compressImage(
  file: File,
  { maxEdge, quality }: PhotoQuality = OVERVIEW_QUALITY
): Promise<Blob> {
  if (!file.type.startsWith("image/")) {
    return file;
  }
  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await loadImage(objectUrl);
    const longest = Math.max(image.naturalWidth, image.naturalHeight);
    if (!longest) {
      return file;
    }
    const scale = Math.min(1, maxEdge / longest);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(image.naturalWidth * scale);
    canvas.height = Math.round(image.naturalHeight * scale);
    const context = canvas.getContext("2d");
    if (!context) {
      return file;
    }
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", quality)
    );
    return blob ?? file;
  } catch {
    return file;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/** 파일 이름에서 확장자. 점이 여러 개여도 마지막 것을 쓴다. 없으면 빈 문자열 */
export function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot > 0 && dot < name.length - 1 ? name.slice(dot + 1) : "";
}

/**
 * 줄인 사진을 baseName 이름의 File 로 돌려준다. 줄였으면 .jpg, 줄이지 못했으면 원래 확장자.
 * 원래 확장자도 없으면 null (업로드 대상에서 뺀다).
 */
export async function compressToFile(
  file: File,
  baseName: string,
  photoQuality: PhotoQuality = OVERVIEW_QUALITY
): Promise<File | null> {
  const blob = await compressImage(file, photoQuality);
  if (blob !== file) {
    return new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
  }
  const extension = extensionOf(file.name);
  if (!extension) return null;
  return new File([file], `${baseName}.${extension}`, { type: file.type });
}
